import {Fragment, useCallback, useEffect, useId, useMemo, useRef} from 'react'
import type {ReactElement, PropsWithChildren, KeyboardEvent, FocusEvent, ReactNode} from 'react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {noop} from '@github-ui/noop'
import {Box, merge} from '@primer/react'
import {clsx} from 'clsx'

import {ActionsProvider} from './ActionsContext'
import {MetadataContainer} from './MetadataContainer'
import {useListViewId} from '../ListView/IdContext'
import {useListViewVariant} from '../ListView/VariantContext'
import {useListViewItems} from '../ListView/ItemsContext'
import {useListViewSelection} from '../ListView/SelectionContext'
import {SelectionProvider} from './SelectionContext'
import {TitleProvider, useListItemTitle} from './TitleContext'
import {DescriptionProvider, useListItemDescription} from './DescriptionContext'
import {StatusProvider, useListItemStatus} from './StatusContext'
import {ActionBar} from './ActionBar'
import type {Metadata} from './Metadata'
import type {Title} from './Title'
import {LeadingBadgeProvider, useListItemLeadingBadge} from './LeadingBadgeContext'
import {TrailingBadgeProvider, useListItemTrailingBadge} from './TrailingBadgeContext'

export type ListItemProps = PropsWithChildren<{
  isSelected?: boolean
  isActive?: boolean

  onEnterKey?: (event: KeyboardEvent) => void
  onKeyDown?: (event: KeyboardEvent<HTMLLIElement>) => void
  onSelect?: (isSelected: boolean) => void
  onFocus?: (event: FocusEvent<HTMLLIElement>) => void

  /**
   * A ListItem title communicates the overall purpose of the ListItem.
   */
  title: ReactElement<typeof Title>

  /**
   * Optional extra elements to display on the right side of the list item. You can optionally wrap individual
   * pieces of content in `ListItem.Metadata` for a consistent appearance.
   */
  metadata?: ReactNode | Array<ReactElement<typeof Metadata>>

  /**
   * Optional menu of additional actions to be shown on the right side of the ListItem. Use `ListItem.ActionBar`.
   */
  secondaryActions?: ReactElement<typeof ActionBar>
  sx?: BetterSystemStyleObject
}>

// minmax(0, max-content) - '0' is required to fix overflow caused by the "metadata" on smaller screen sizes
// '1fr' is required to fill available space ('auto' will cause content to overflow)
const commonDefaultGridTemplateColumns = 'min-content min-content minmax(7em, 1fr) minmax(0, max-content)'
const commonCompactGridTemplateColumns =
  'min-content min-content minmax(2em, max-content) minmax(0, auto) minmax(min-content, max-content)'
const commonListItemSx: BetterSystemStyleObject = {
  position: 'relative',
  display: 'grid',
  minHeight: '2rem' /* 32px */,
  fontSize: 1,
  gap: 1,
  color: 'fg.default',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: '"selection status primary metadata actions" "selection status main-content metadata actions"',
  gridTemplateColumns: `${commonDefaultGridTemplateColumns} min-content`,

  ':not(:last-child)': {
    borderBottom: '1px solid',
    borderColor: 'border.subtle',
  },

  '&.listviewitem-compact': {
    minHeight: '1rem',
  },

  '&:hover': {
    backgroundColor: 'canvas.subtle',
  },

  '&.listviewitem-selected': {
    backgroundColor: 'accent.subtle',
    boxShadow: '0 0 0 1px var(--color-accent-fg)',
    zIndex: 1,

    '&:hover': {
      backgroundColor: 'accent.subtle',
    },
  },

  '&:focus-visible': {
    outline: '2px solid var(--color-accent-fg)',
    outlineOffset: '-2px',
    boxShadow: 'none',
  },
}

const compactVariantListItemSx: BetterSystemStyleObject = {
  gridTemplateRows: 'auto',
  gridTemplateAreas: '"selection status primary main-content metadata actions"',
  gridTemplateColumns: `${commonCompactGridTemplateColumns} min-content`,
}

const ListItemBase = ({
  children,
  isSelected = false,
  isActive = false,
  onEnterKey = noop,
  onSelect = noop,
  onFocus = noop,
  title,
  metadata,
  secondaryActions,
  sx: externalListItemSx,
  ...props
}: ListItemProps): JSX.Element => {
  const {idPrefix} = useListViewId()
  const {isSelectable} = useListViewSelection()
  const {variant} = useListViewVariant()
  const {anyItemsWithActionBar, hasResizableActionsWithActionBar} = useListViewItems()
  const uniqueIdSuffix = useId()
  const {status: labelStatus} = useListItemStatus()
  const {title: labelTitle} = useListItemTitle()
  const {description: labelDescription} = useListItemDescription()
  const {leadingBadge: labelLeadingBadge} = useListItemLeadingBadge()
  const {trailingBadge: labelTrailingBadge} = useListItemTrailingBadge()
  const itemRef = useRef<HTMLLIElement>(null)

  // This allows the actions to show in the action bar depending on the screen size. These actions
  // will move into a dropdown menu when the screen isn't big enough to fit them side by side.
  // Edge case: if some items have resizable actions and some don't have an actionbar,
  // column alignment will be off.
  const gridTemplateSx =
    anyItemsWithActionBar && hasResizableActionsWithActionBar
      ? variant === 'compact'
        ? {gridTemplateColumns: `${commonCompactGridTemplateColumns} minmax(0, auto)`}
        : {gridTemplateColumns: `${commonDefaultGridTemplateColumns} auto`}
      : {}

  const baseListItemSx =
    variant === 'compact'
      ? merge<BetterSystemStyleObject>(commonListItemSx, compactVariantListItemSx)
      : commonListItemSx
  const flexibleListItemSx: BetterSystemStyleObject = merge<BetterSystemStyleObject>(baseListItemSx, gridTemplateSx)
  const listItemSx = externalListItemSx
    ? merge<BetterSystemStyleObject>(flexibleListItemSx, externalListItemSx)
    : flexibleListItemSx

  useEffect(() => {
    if (!itemRef.current) return
    // Focus the element unless another element in the document is specifically focused
    if (isActive && document.activeElement?.tagName === 'BODY') {
      itemRef.current.focus()
    }
  })

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLLIElement>) => {
      // eslint-disable-next-line no-restricted-syntax
      switch (e.key) {
        case 'Enter':
          // Invoke onEnterKey when the user presses enter
          onEnterKey(e)
          break
        case ' ':
          // When the user presses Space
          if (!isSelectable) break
          if (itemRef?.current !== document.activeElement) break // if the focus is inside the listitem, do not select the item
          e.preventDefault()
          onSelect(!isSelected)
          break
        case 'Escape':
          itemRef?.current?.focus()
          break
        default:
          break
      }
    },
    [onEnterKey, isSelectable, onSelect, isSelected],
  )

  useEffect(() => {}, [metadata, secondaryActions])

  const getAriaLabel = useCallback(() => {
    const hasSecondaryActions = !!secondaryActions
    const hasMetadata = (Array.isArray(metadata) && metadata.length > 0) || (metadata && !Array.isArray(metadata))
    let labelAdditionalInfo = ''
    if (hasMetadata && hasSecondaryActions) {
      labelAdditionalInfo = 'More info and actions available.'
    } else if (hasMetadata) {
      labelAdditionalInfo = 'More info available.'
    } else if (hasSecondaryActions) {
      labelAdditionalInfo = 'Actions available.'
    }

    // Announce leading badge, title, trailing badge, then status for better screen reader experience
    const main = [labelLeadingBadge, labelTitle, labelTrailingBadge, labelStatus].filter(str => str.trim()).join(': ')
    return [main, labelDescription, labelAdditionalInfo].filter(str => str.trim()).join('. ')
  }, [secondaryActions, metadata, labelLeadingBadge, labelTitle, labelTrailingBadge, labelStatus, labelDescription])

  return (
    <Box
      as="li"
      ref={itemRef}
      id={`${idPrefix}-listview-node-${uniqueIdSuffix}`}
      role="listitem"
      className={clsx(
        isSelected && 'listviewitem-selected',
        variant === 'compact' && 'listviewitem-compact',
        'listviewitem',
      )}
      tabIndex={-1} // Handled by useFocusZone with roving tabIndex
      aria-label={getAriaLabel()}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      sx={listItemSx}
      {...props}
    >
      {title}
      {children}
      {Array.isArray(metadata) && metadata.length > 0 ? (
        <MetadataContainer>
          {metadata.map((metadataItem, index) => (
            <Fragment key={index}>{metadataItem}</Fragment>
          ))}
        </MetadataContainer>
      ) : (
        metadata && !Array.isArray(metadata) && <MetadataContainer>{metadata}</MetadataContainer>
      )}
      {secondaryActions ?? (anyItemsWithActionBar && <ActionBar />)}
    </Box>
  )
}

export const ListItem = ({
  children,
  isSelected = false,
  onSelect = noop,
  ...rest
}: PropsWithChildren<ListItemProps>): JSX.Element => {
  const {setSelectedCount} = useListViewSelection()

  useEffect(() => {
    setSelectedCount(count => (isSelected ? count + 1 : Math.max(0, count - 1)))
  }, [isSelected, setSelectedCount])

  const selectionProviderProps = useMemo(() => ({isSelected, onSelect}), [isSelected, onSelect])

  return (
    <ActionsProvider>
      <SelectionProvider value={selectionProviderProps}>
        <LeadingBadgeProvider>
          <TitleProvider>
            <TrailingBadgeProvider>
              <DescriptionProvider>
                <StatusProvider>
                  <ListItemBase isSelected={isSelected} onSelect={onSelect} {...rest}>
                    {children}
                  </ListItemBase>
                </StatusProvider>
              </DescriptionProvider>
            </TrailingBadgeProvider>
          </TitleProvider>
        </LeadingBadgeProvider>
      </SelectionProvider>
    </ActionsProvider>
  )
}

try{ ListItemBase.displayName ||= 'ListItemBase' } catch {}
try{ ListItem.displayName ||= 'ListItem' } catch {}